/**
 * roundValues.ts – Utility for rounding all numeric values in a value tree.
 *
 * Useful as a `transformResult` callback in `createEvaluate()` to keep
 * AI responses concise and free of floating-point noise.
 *
 * @example
 * ```ts
 * const evaluate = createEvaluate({
 *   api: myApi,
 *   transformResult: (result) => roundValues(result, 4),
 * });
 * ```
 */

/**
 * Recursively rounds all numeric values in an object/array/primitive
 * to the given decimal precision.
 */
export function roundValues(value: any, precision: number): any {
  if (typeof value === "number") {
    const factor = Math.pow(10, precision);
    return Math.round(value * factor) / factor;
  }
  if (Array.isArray(value)) {
    return value.map((v) => roundValues(v, precision));
  }
  if (value && typeof value === "object" && value.constructor === Object) {
    const result: any = {};
    for (const key of Object.keys(value)) {
      result[key] = roundValues(value[key], precision);
    }
    return result;
  }
  return value;
}

